import React, { ButtonHTMLAttributes, ReactNode, useState } from 'react';
import styled, { css } from 'styled-components';
import { colors } from '../../styles/colors';

export type ButtonVariant = 'primary' | 'secondary' | 'text' | 'border' | 'tertiary';
export type ButtonSize = 'small' | 'medium' | 'large';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  disabled?: boolean;
  className?: string;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  color?: string;
}

interface StyledButtonProps {
  $variant: ButtonVariant;
  $size: ButtonSize;
  $fullWidth: boolean;
  $isHovered: boolean;
  $isActive: boolean;
  disabled?: boolean;
  color?: string;
}

const getButtonStyles = (props: StyledButtonProps) => {
  const {$variant, disabled, $isHovered, $isActive, color} = props;

  // Базовые стили для всех вариантов
  const baseStyles = css`
      border-radius: 27px;
      font-weight: 600;
      transition: all 0.2s ease-in-out;
      cursor: ${disabled ? 'not-allowed' : 'pointer'};
      display: flex;
      align-items: center;
      justify-content: center;
      border: none;
      outline: none;
      text-decoration: none;
  `;

  if ($variant === 'primary') {
    let bgColor = colors.accentPrimary;
    let textColor = colors.accentWhite;

    if (disabled) {
      bgColor = colors.accentQuaternary3;
      textColor = colors.accentQuaternary5;
    } else if ($isActive) {
      bgColor = colors.clickState;
    } else if ($isHovered) {
      bgColor = colors.hoverState;
    }

    return css`
        ${baseStyles}
        background-color: ${bgColor};
        color: ${textColor};
    `;
  }
  if ($variant === 'border') {
    let bgColor = 'transparent';
    let textColor = colors.accentPrimary;

    if (disabled) {
      bgColor = colors.accentQuaternary3;
      textColor = colors.accentQuaternary5;
    } else if ($isActive) {
      bgColor = colors.clickState;
      textColor = colors.accentWhite;
    } else if ($isHovered) {
      bgColor = colors.accentPrimary;
      textColor = colors.accentWhite;
    }

    return css`
        ${baseStyles}
        border: 2px solid ${colors.accentPrimary};
        background-color: ${bgColor};
        color: ${textColor};
    `;
  }
  // Стили для secondary варианта
  if ($variant === 'secondary') {
    let bgColor = colors.accentQuaternary;
    let textColor = colors.accentPrimary;
    let borderColor = 'transparent';

    if (disabled) {
      bgColor = colors.accentQuaternary2;
      textColor = colors.accentQuaternary5;
    } else if ($isActive) {
      bgColor = colors.clickState;
      textColor = colors.accentWhite;
    } else if ($isHovered) {
      bgColor = colors.hoverState;
      textColor = colors.accentWhite;
    }

    return css`
        ${baseStyles}
        background-color: ${bgColor};
        color: ${textColor};
        border: 1px solid ${borderColor};
    `;
  }

  if ($variant === 'tertiary') {
    let bgColor = colors.accentWhite;
    let textColor = colors.accentPrimary;
    if (disabled) {
      bgColor = colors.accentQuaternary2;
      textColor = colors.accentQuaternary5;
    } else if ($isActive) {
      bgColor = colors.accentPrimary;
      textColor = colors.accentWhite;
    } else if ($isHovered) {
      bgColor = colors.accentTertiary;
      textColor = colors.accentPrimary;
    }
    return css`
      ${baseStyles}
      background-color: ${bgColor};
      color: ${textColor};
      box-shadow: none;
    `;
  }

  // Стили для text варианта
  return css`
      ${baseStyles}
      background-color: transparent;
      color: ${color ? color : (disabled ? colors.accentQuaternary5 : colors.accentSecondary)};
      padding: 0 !important;
      height: auto !important;
      display: inline !important;

      &:hover {
          text-decoration: ${color ? 'none' : disabled ? 'none' : 'underline'};
      }
  `;
};

const getSizeStyles = (size: ButtonSize) => {
  switch (size) {
    case 'small':
      return css`
          height: 48px;
          padding: 0 16px;
          font-size: 14px;
      `;
    case 'large':
      return css`
          height: 54px;
          padding: 0 32px;
          font-size: 18px;
      `;
    case 'medium':
    default:
      return css`
          height: 42px;
          padding: 0 24px;
          font-size: 16px;
      `;
  }
};

const StyledButton = styled.button<StyledButtonProps>`
    ${(props) => getButtonStyles(props)}
    ${(props) => getSizeStyles(props.$size)}
    width: ${(props) => (props.$fullWidth ? '100%' : 'auto')};
    opacity: ${(props) => (props.disabled ? 0.7 : 1)};
    gap: 8px;
`;

// Обновленный IconWrapper теперь наследует цвет текста родительского элемента
const IconWrapper = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: inherit; /* Наследует цвет от родительского элемента (кнопки) */

    /* Применение текущего цвета к SVG внутри обертки */

    svg {
        color: inherit;
        fill: currentColor !important;
        stroke: currentColor;

        /* Применяем наш цвет ко всем внутренним элементам SVG */

        * {
            fill: currentColor !important;
            color: inherit;
        }
    }

    /* Применяем текущий цвет ко всем дочерним элементам */

    * {
        color: inherit;
    }
`;

export const Button: React.FC<ButtonProps> = ({
                                                children,
                                                variant = 'primary',
                                                size = 'medium',
                                                fullWidth = false,
                                                disabled = false,
                                                className,
                                                startIcon,
                                                endIcon,
                                                ...props
                                              }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isActive, setIsActive] = useState(false);

  // Обертки IconWrapper уже наследуют цвет кнопки через CSS (color: inherit),
  // поэтому нам не нужно модифицировать сами иконки программно
  const processedStartIcon = startIcon;
  const processedEndIcon = endIcon;

  return (
    <StyledButton
      color={props.color}
      $variant={variant}
      $size={size}
      $fullWidth={fullWidth}
      $isHovered={isHovered}
      $isActive={isActive}
      disabled={disabled}
      className={className}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setIsActive(false);
      }}
      onMouseDown={() => setIsActive(true)}
      onMouseUp={() => setIsActive(false)}
      {...props}
    >
      {processedStartIcon && <IconWrapper>{processedStartIcon}</IconWrapper>}
      {children}
      {processedEndIcon && <IconWrapper>{processedEndIcon}</IconWrapper>}
    </StyledButton>
  );
};
